import type { Logger } from "@expert/logging";
import type { SendExpertAssistMessage } from "@expert/shared-types";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { trackFullStoryEvent } from "@expert/monitoring";
import { Group, Pill, Progress, Stack } from "@mantine/core";
import { type JSX, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";

import type { GuideActionOption, GuideOption, GuideStep } from "../logic-builder";
import type { CloseGuideSource, GuideSessionContext } from "../types";

import { MarkdownViewer } from "../../formatting";
import { isActivationAction, isPinAction, useDeviceActivation } from "../device-activation";
import { getRoomId, navigateBack, startSimplrSession, triggerNextStep } from "../logic-builder";
import { sanitizeFreeText } from "../logic-builder/sanitizeFreeText";
import { useDelayTracking } from "../logic-builder/useDelayTracking";
import { useGuideSession, useGuideStore } from "../store";
import { ActivationNotices } from "./ActivationNotices";
import { AskAssist } from "./AskAssist";
import { GuideActionGroup } from "./GuideActionGroup";
import classes from "./GuideCard.module.css";
import { GuideCardFreeTextInput } from "./GuideCardFreeTextInput";
import { GuideCardInput } from "./GuideCardInput";
import { GuideCloseConfirmation } from "./GuideCloseConfirmation";
import { GuideHomeConfirmation } from "./GuideHomeConfirmation";
import { GuideMenu } from "./GuideMenu";
import { GuideNavigation } from "./GuideNavigation";
import { GuideTag } from "./GuideTag";
import { markdownOverrides } from "./markdownOverrides";
import { buildUnsupportedDeviceAssistPrompt } from "./unsupportedDeviceAssist";
import { UnsupportedDeviceAssistButton } from "./UnsupportedDeviceAssistButton";

interface GuideCardProps {
    logger: Logger;
    onGuideClose: (source: CloseGuideSource) => void;
    onAskAssist?: () => void;
    opened: boolean;
    sendMessage: SendExpertAssistMessage;
    session: GuideSessionContext;
    step: GuideStep;
}

const FREE_TEXT_REQUIRED_ERROR = "Please enter a response";

const validateInput = (val: string, validationRegExp: string) => {
    const regex = new RegExp(validationRegExp);
    const isValid = regex.test(val);
    return isValid;
};

export function GuideCard({
    logger: loggerProp,
    onGuideClose,
    onAskAssist,
    opened,
    sendMessage,
    session,
    step,
}: GuideCardProps): JSX.Element | null {
    const { callSid = "", omitExternalTicketId, sessionId } = session;
    const {
        actions,
        alternateText,
        currentStep,
        dropdownInput,
        freeTextInput,
        hasPreviousSteps,
        messageId,
        progress,
        prompt,
        showAskAssist,
        showDeviceConfirmation,
        showVerizonSimType,
        textInput,
    } = step;
    const logger = useMemo(() => loggerProp.child({ module: "GuideCard", supportTeam: "solve" }), [loggerProp]);
    const { dispatcher } = useReactAnalytics();
    const enhancedDispatcher = callSid ? dispatcher.withIdentities({ CallSid: callSid }) : dispatcher;

    const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);
    const [isHomeConfirmationOpen, setIsHomeConfirmationOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<GuideOption | undefined>(undefined);
    const [validationErrorMessage, setValidationErrorMessage] = useState<string | undefined>();
    const [isLoadingStep, setIsLoadingStep] = useState(false);
    const [isWaitingForRoom, setIsWaitingForRoom] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [freeText, setFreeText] = useState("");
    const {
        analyticsInfo,
        currentGuideName,
        errorStateSessionId,
        isErrorState,
        isWaitingForNextStep,
        sessionMetadata: storedSessionMetadata,
        uiMetadata: { closeConfirmation },
    } = useGuideSession(sessionId);

    // step has loaded, so toggle loading state off. Keyed on the step's message, not its text:
    // consecutive steps can share text, and Back/Next between them would otherwise never clear loading.
    const stepKey = messageId ?? currentStep;
    const [previousStep, setPreviousStep] = useState(stepKey);
    if (stepKey !== previousStep) {
        setPreviousStep(stepKey);
        setIsLoadingStep(false);
        setIsWaitingForRoom(false);
    }

    const extraData = useMemo(() => ({ ...analyticsInfo, guide: currentGuideName }), [analyticsInfo, currentGuideName]);
    const { completedStepCount, currentStepId, guide: flowName } = extraData;

    const isLoading = isLoadingStep || isWaitingForRoom || isWaitingForNextStep;

    const activation = useDeviceActivation({
        actions,
        currentStep,
        dispatcher: enhancedDispatcher,
        extraData,
        logger,
        session,
        sessionMetadata: storedSessionMetadata,
    });

    useDelayTracking({
        currentStepId,
        logger,
        isLoading,
        dispatcher: enhancedDispatcher,
        opened,
    });

    const onGuideCompleted = useEffectEvent(() => {
        logger.info({ ...extraData }, `Solve Guide | Completed guide`);
        void enhancedDispatcher.dispatchBusinessEvent(`Guide_CompletedGuide`, {
            ...extraData,
        });
        trackFullStoryEvent("Guide_CompletedGuide", {
            ...extraData,
        });
    });

    useEffect(() => {
        if (progress === 100) onGuideCompleted();
    }, [progress]);

    // guide was just opened so set loading state
    // check if an existing room exists for a given session when guide opens
    const previousOpenedRef = useRef(opened);
    useEffect(() => {
        const wasOpened = previousOpenedRef.current;
        previousOpenedRef.current = opened;
        if (!opened || wasOpened) return;
        // In error state nothing creates the new room until Restart, which loading would disable
        setIsWaitingForRoom(!isErrorState && !getRoomId(sessionId, omitExternalTicketId));
        // oxlint-disable-next-line exhaustive-deps
    }, [opened]);

    const clearInputState = () => {
        setSelectedOption(undefined);
        setValidationErrorMessage(undefined);
        setInputValue("");
        setFreeText("");
        useGuideStore.getState().clearSelectedOptions(sessionId);
    };

    const validateDropdown = () => {
        const isValid = !dropdownInput || !dropdownInput.required || selectedOption;
        if (!isValid) {
            setValidationErrorMessage(dropdownInput.validationError ?? "Please select an option");
            logger.info(
                { validationError: dropdownInput.validationError, ...extraData },
                "Solve Guide | Click next (navigate to next step) with dropdown validation error",
            );
        }
        return isValid;
    };

    const validateTextInput = () => {
        const isValid = !textInput || validateInput(inputValue, textInput.validationRegExp);
        if (!isValid) {
            setValidationErrorMessage(textInput.validationError);
            logger.info(
                { validationError: textInput.validationError, ...extraData },
                "Solve Guide | Click next (navigate to next step) with input validation error",
            );
        }
        return isValid;
    };

    const validateFreeText = () => {
        // validate what will be sent: input that was only markup sanitizes to empty
        const isValid = !freeTextInput || sanitizeFreeText(freeText).length > 0;
        if (!isValid) {
            setValidationErrorMessage(FREE_TEXT_REQUIRED_ERROR);
            logger.info({ ...extraData }, "Solve Guide | Click next (navigate to next step) with empty free text");
        }
        return isValid;
    };

    const onNext = async (option: GuideActionOption) => {
        if (!option.skipsValidation && (!validateDropdown() || !validateTextInput())) return;
        // the typed text is a free-text step's reply, so it is required even for a skipsValidation button
        if (!validateFreeText()) return;

        setIsLoadingStep(true);
        const action = option.customAction;
        let nextStep: string | undefined;

        if (action === "closeGuide") {
            setIsLoadingStep(false);
            onClose("cta");
            return;
        }
        if (action === "goBack") {
            navigateBack({ restartFlow: false });
            return;
        }
        if (isActivationAction(action)) {
            const result = await activation.handleAction(action, {
                hasDropdown: !!dropdownInput,
                inputValue,
                option,
                selectedOption,
            });
            if (result.status === "recoveryNeeded") {
                // The action skipped its request; stay on this step so the expert can follow the notice.
                setIsLoadingStep(false);
                return;
            }
            if (result.status === "needsDropdownSelection") {
                // Release the loading flag before bailing out: `validateDropdown` reports false only for a
                // *required* dropdown, so without this an optional-dropdown step (or one whose button sets
                // `skipsValidation`) would sit spinning with every action disabled and no error shown.
                setIsLoadingStep(false);
                return validateDropdown();
            }
            nextStep = result.nextStep;
        }

        const nextStepOption = selectedOption ?? option;
        // like a dropdown step, a free-text step sends its answer and the button only submits it: the typed text
        // is the reply (interaction.data.text), which the chat server sanitizes
        const sendsFreeText = !!freeTextInput && !nextStep;
        const freeTextValue = sanitizeFreeText(freeText);
        logger.info({ guideOption: nextStepOption, ...extraData }, "Solve Guide | Click next (navigate to next step)");
        const onBeforeSend = () => useGuideStore.getState().setIsWaitingForNextStep(sessionId, true);
        if (sendsFreeText) {
            triggerNextStep(freeTextValue, logger, onBeforeSend, { isFreeText: true });
        } else {
            triggerNextStep(nextStep ?? nextStepOption.value, logger, onBeforeSend);
        }
        clearInputState();

        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_NextStep`, {
            button: "NextStep",
            selectedOption: nextStepOption.value,
            ...(sendsFreeText && { freeTextLength: freeTextValue.length }),
            ...extraData,
        });
    };

    const onBack = () => {
        logger.info({ ...extraData }, "Solve Guide | Click back (navigate to previous step)");
        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_PreviousStep`, {
            button: "PreviousStep",
            ...extraData,
        });
        navigateBack({ restartFlow: false });
        clearInputState();
        setIsLoadingStep(true);
    };

    const onRestart = () => {
        const eventName = isErrorState ? `RestartErrorState` : `RestartGuide`;
        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_${eventName}`, {
            button: "RestartGuide",
            ...extraData,
        });

        /*
            If the guided flow encounters the global error flow, restart the
            logic builder session with a new unique session ID based on the
            workspace session, as the previous ID is linked to the broken session.
        */
        if (isErrorState) {
            // Error-state restart spins up a new Simplr session — drop any stale retry block, and the
            // customer's answers: a repeat restart can reuse the same room key, so scoping alone won't do.
            activation.reset();
            useGuideStore.getState().clearDeviceActivationState(sessionId);
            if (errorStateSessionId) {
                logger.info({ ...extraData, errorStateSessionId }, "Solve Guide | Error state - Restart new session");
                startSimplrSession({
                    sessionId: errorStateSessionId,
                    metadata: storedSessionMetadata,
                    omitExternalTicketId,
                });
            } else {
                // we should never hit this block because isErrorState and
                // errorStateSessionId get set at the same time in useGuideManager
                logger.info({ ...extraData }, "Solve Guide | Error state - Restart new session");
                startSimplrSession({
                    sessionId: `${sessionId}_RESTARTED`,
                    metadata: storedSessionMetadata,
                    omitExternalTicketId,
                });
                useGuideStore.getState().setErrorStateSessionId(sessionId);
            }
            setIsLoadingStep(true);
            return;
        }

        logger.info({ ...extraData }, "Solve Guide | Click return to home (navigate to first step)");
        setIsHomeConfirmationOpen(true);
    };

    const onRestartConfirmed = () => {
        const { resetGuide, setDisableAutomaticOpen } = useGuideStore.getState();
        logger.info({ ...extraData }, "Solve Guide | Click confirm return to home");
        setIsHomeConfirmationOpen(false);
        navigateBack({ restartFlow: true });
        resetGuide(sessionId);
        setDisableAutomaticOpen(sessionId, false);
        // GuideCard isn't remounted on restart, so clear the retry block explicitly — a restart is a fresh
        // attempt and must not inherit a disabled Activate button from the previous run's inputs.
        activation.reset();
        setIsLoadingStep(true);
    };

    const onClose = (source: "cta" | "navigation") => {
        logger.info({ ...extraData, source }, "Solve Guide | Click close guide");

        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_CloseGuide`, {
            button: "CloseGuide",
            source,
            ...extraData,
        });

        if (progress === 100 || !closeConfirmation) {
            // Closing a finished guide ends it, so drop the customer's answers. Not done on reaching 100%:
            // the expert can still go Back or retry from a final success/failure step.
            if (progress === 100) useGuideStore.getState().clearDeviceActivationState(sessionId);
            useGuideStore.getState().setDisableAutomaticOpen(sessionId, true);
            onGuideClose("closeButton");
        } else {
            setIsCloseConfirmationOpen(true);
        }
    };

    const onCloseConfirmed = () => {
        logger.info({ ...extraData }, `Solve Guide | Click Confirm close guide`);
        useGuideStore.getState().setDisableAutomaticOpen(sessionId, true);
        setIsCloseConfirmationOpen(false);
        onGuideClose("closeButton");
    };

    const onDropdownClick = (option: GuideOption) => {
        logger.info({ guideOption: option, ...extraData }, `Solve Guide | Selected dropdown option`);
        setValidationErrorMessage(undefined);
        setSelectedOption(option);
    };

    const contentWithOverride = activation.shouldUseAlternateText && alternateText ? alternateText : currentStep;

    // `isOptionAvailable` hides an action this expert isn't enabled for, such as typing in a Verizon IMEI.
    const { isOptionAvailable } = activation;
    const visibleActions = useMemo(
        () => actions.filter((option) => !option.isHidden && isOptionAvailable(option)),
        [actions, isOptionAvailable],
    );
    // The PIN is a secret, so the input is masked with a show/hide toggle on the steps that verify it.
    const isPinStep = visibleActions.some((option) => isPinAction(option.customAction));
    const visibleDropdownOptions = useMemo(
        () => dropdownInput?.options.filter((option) => !option.isHidden && isOptionAvailable(option)) ?? [],
        [dropdownInput, isOptionAvailable],
    );

    const unsupportedAssist = showAskAssist
        ? buildUnsupportedDeviceAssistPrompt({ ...activation.assistContext, authoredPrompt: prompt })
        : undefined;

    return (
        <>
            <Stack className={classes.guideCard} h="auto" w="100%">
                <Progress
                    bg="transparent"
                    classNames={{ section: classes.progressSection }}
                    color="success.4"
                    h="0.25rem"
                    mt="0rem"
                    value={hasPreviousSteps ? progress : 0}
                />
                <Stack gap="0" p="sm" pt="xs" pb="xs" justify="space-between" w="100%">
                    <Group w="100%" justify="space-between" wrap="wrap">
                        <Group align="center" gap="xs" p="0">
                            <Pill.Group>{<GuideTag>{`${progress}% complete`}</GuideTag>}</Pill.Group>
                        </Group>
                    </Group>
                    <Stack>
                        <Stack gap="0rem" align="flex-start">
                            <MarkdownViewer content={contentWithOverride} options={{ overrides: markdownOverrides }} />
                            <ActivationNotices
                                activation={activation}
                                isLoading={isLoading}
                                showAskAssist={showAskAssist}
                                showDeviceConfirmation={showDeviceConfirmation}
                                showVerizonSimType={showVerizonSimType}
                            />
                            {dropdownInput && (
                                <GuideMenu
                                    label={dropdownInput.label}
                                    options={activation.dropdownOptions(
                                        visibleDropdownOptions,
                                        dropdownInput.action?.customAction,
                                    )}
                                    onClick={onDropdownClick}
                                    sessionId={sessionId}
                                    validationError={validationErrorMessage}
                                />
                            )}
                            {textInput && (
                                <GuideCardInput
                                    textInput={textInput}
                                    value={inputValue}
                                    onInput={setInputValue}
                                    masked={isPinStep}
                                    validationError={validationErrorMessage}
                                />
                            )}
                            {freeTextInput && (
                                <GuideCardFreeTextInput
                                    freeTextInput={freeTextInput}
                                    value={freeText}
                                    onInput={(val) => {
                                        setValidationErrorMessage(undefined);
                                        setFreeText(val);
                                    }}
                                    validationError={validationErrorMessage}
                                />
                            )}
                            {prompt && !showAskAssist && (
                                <AskAssist
                                    callSid={callSid}
                                    extraData={extraData}
                                    prompt={prompt}
                                    sendMessage={sendMessage}
                                />
                            )}
                        </Stack>
                        <Group justify="space-between" gap="0rem">
                            <GuideNavigation
                                hasCompletedSteps={completedStepCount > 0}
                                isErrorState={isErrorState}
                                isLoading={isLoading}
                                onBack={onBack}
                                onRestart={onRestart}
                            />
                            <Group gap="sm" align="flex-end">
                                {unsupportedAssist && (
                                    <UnsupportedDeviceAssistButton
                                        callSid={callSid}
                                        extraData={extraData}
                                        prompt={unsupportedAssist.prompt}
                                        promptType={unsupportedAssist.promptType}
                                        deviceMake={activation.assistContext.make}
                                        deviceModel={activation.assistContext.model}
                                        sendMessage={sendMessage}
                                        onAskAssist={onAskAssist}
                                    />
                                )}
                                <GuideActionGroup
                                    currentStep={stepKey}
                                    disableActivate={activation.isActivationBlocked}
                                    isLoading={isLoading}
                                    options={visibleActions}
                                    onClick={(option) => void onNext(option)}
                                />
                            </Group>
                        </Group>
                    </Stack>
                </Stack>
            </Stack>
            {closeConfirmation && (
                <GuideCloseConfirmation
                    callSid={callSid}
                    extraData={extraData}
                    guideId={flowName ?? "Guided flow"}
                    isOpen={isCloseConfirmationOpen}
                    options={closeConfirmation.options}
                    title={closeConfirmation.title}
                    onCancel={() => {
                        logger.info({ ...extraData }, `Solve Guide | Cancelled close guide`);
                        setIsCloseConfirmationOpen(false);
                    }}
                    onClose={onCloseConfirmed}
                />
            )}
            <GuideHomeConfirmation
                callSid={callSid}
                extraData={extraData}
                isOpen={isHomeConfirmationOpen}
                onClose={() => {
                    logger.info({ ...extraData }, `Solve Guide | Cancelled return to home`);
                    setIsHomeConfirmationOpen(false);
                }}
                onSubmit={onRestartConfirmed}
            />
        </>
    );
}
